// src/utils/layout/columnPacking.js
//
// 2D sutun paketleme algoritmasi — dokumanlari sutunlara yerlestirme.

import * as THREE from 'three';
import { dxfDrawCore } from '../../../modules/dxf';
import { createMainFrameParts } from '../../../utils/simpleTransformFunctions';
import { LAYOUT_GROUP_ORDER } from '../../../constants/projectTypes';

export function tryPlaceInCol(col, w, h, maxColumnHeight, DOC_OFFSET) {
  for (let i = col.docs.length - 1; i >= 0; i--) {
    const P = col.docs[i];
    if (P.h < h) continue;
    const cx = P.subX + P.w + DOC_OFFSET;
    const cy = P.subY + P.h - h;
    if (cx + w > col.maxWidth) continue;
    if (cy < 0 || cy + h > maxColumnHeight) continue;
    let blocked = false;
    for (let j = 0; j < col.docs.length; j++) {
      if (j === i) continue;
      const Q = col.docs[j];
      if (!(cx + w + DOC_OFFSET <= Q.subX || Q.subX + Q.w + DOC_OFFSET <= cx ||
            cy + h + DOC_OFFSET <= Q.subY || Q.subY + Q.h + DOC_OFFSET <= cy)) {
        blocked = true; break;
      }
    }
    if (blocked) continue;
    return { subX: cx, subY: cy };
  }
  const yBottom = col.height + (col.docs.length > 0 ? DOC_OFFSET : 0);
  if (yBottom + h <= maxColumnHeight) return { subX: 0, subY: yBottom };
  return null;
}

export function positionColumns(groupCols, pinnedCols, layoutSettings, maxColumnHeight, DOC_OFFSET) {
  const DIRECTION = layoutSettings.direction || 'ltr';
  const VERTICAL_ALIGN = layoutSettings.verticalAlign || 'bottom';
  const GROUP_GAP = DOC_OFFSET * 2;

  const computeShift = (colHeight) => {
    const free = maxColumnHeight - colHeight;
    if (VERTICAL_ALIGN === 'top') return 0;
    if (VERTICAL_ALIGN === 'center') return free / 2;
    return free;
  };

  let cursorX = 0;
  const allCols = [];

  const positionCol = (col) => {
    col.x = cursorX;
    cursorX += col.maxWidth + DOC_OFFSET;
    col.docs.forEach(item => {
      item.group.position.set(col.x + item.subX, -DOC_OFFSET - item.subY, 0);
    });
    const shift = computeShift(col.height);
    if (Math.abs(shift) > 0.01) {
      col.docs.forEach(item => { item.group.position.y -= shift; });
    }
    allCols.push(col);
  };

  LAYOUT_GROUP_ORDER.forEach(gk => {
    const arr = groupCols[gk];
    if (!arr || arr.length === 0) return;
    arr.forEach(positionCol);
    cursorX += GROUP_GAP - DOC_OFFSET;
  });
  pinnedCols.forEach(positionCol);

  if (DIRECTION === 'rtl') {
    allCols.forEach(col => {
      col.docs.forEach(item => {
        item.group.position.x = cursorX - (item.group.position.x + item.w);
      });
    });
  }

  return { allCols, totalWidth: cursorX };
}

export function createMainFrameGroup(totalWidth, frameHeight, DOC_OFFSET) {
  const frameWidth = totalWidth + DOC_OFFSET;
  const mainFrameParts = createMainFrameParts(frameWidth, frameHeight, DOC_OFFSET);
  const group = new THREE.Group();
  dxfDrawCore(group, mainFrameParts, [], "scene");
  group.userData.isFrameContent = true;
  return { group, frameWidth };
}

export function compactColumn(col, DOC_OFFSET, maxColumnHeight, VERTICAL_ALIGN, DIRECTION, maxX) {
  col.docs.forEach(item => {
    item.group.position.set(0, 0, 0);
    item.group.updateMatrixWorld(true);
  });
  const measured = col.docs.map(item => {
    const bbox = new THREE.Box3().setFromObject(item.group);
    const realH = bbox.max.y - bbox.min.y;
    const validH = (Number.isFinite(realH) && realH > 50 && realH < 10000) ? realH : item.docDef.h;
    const topOffset = Number.isFinite(bbox.max.y) ? bbox.max.y : 0;
    return { item, realH: validH, topOffset };
  });
  let runningY = 0;
  measured.forEach(({ item, realH }, idx) => {
    if (idx > 0) runningY += DOC_OFFSET;
    item.h = realH;
    item.subY = runningY;
    runningY += realH;
  });
  col.height = runningY;
  const free = maxColumnHeight - col.height;
  const shift = VERTICAL_ALIGN === 'top' ? 0 : VERTICAL_ALIGN === 'center' ? free / 2 : free;
  measured.forEach(({ item, topOffset }) => {
    const contentTopY = -DOC_OFFSET - item.subY - (Math.abs(shift) > 0.01 ? shift : 0);
    const xPos = DIRECTION === 'rtl' ? maxX - (col.x + item.subX + item.w) : col.x + item.subX;
    item.group.position.set(xPos, contentTopY - topOffset, 0);
  });
}
